// interiorQuotationApi.js - Stage Interior Quotations API (JSON snapshots + draft/submit/discard)
// + Interior files helpers for line-item images.
// Dev: `/Stage` via setupProxy.js (avoids CORS; API only allows app.innoworx.ai).
import { fetchAuthSession } from 'aws-amplify/auth';

const DEFAULT_API_BASE =
  process.env.REACT_APP_API_BASE_URL || 'https://api.innoworx.ai/Stage';

const API_BASE_URL =
  process.env.REACT_APP_INTERIOR_QUOTATION_API_BASE_URL ||
  (process.env.NODE_ENV === 'development' ? '/Stage' : DEFAULT_API_BASE);

export const INTERIOR_QUOTATION_PERMISSIONS = {
  upload: 'interior.quotation.upload',
  view: 'interior.quotation.view',
  decide: 'interior.quotation.decide',
  reopen: 'interior.quotation.reopen',
  supersede: 'interior.quotation.supersede',
};

const resolveSiteId = (site) =>
  site?.site_id ?? site?.siteId ?? site?.id ?? site?._id ?? site?.site_code ?? site?.siteCode ?? '';

const resolveSiteName = (site) =>
  site?.site_name || site?.name || site?.siteCode || site?.site_code || 'Unnamed Site';

const makeApiRequest = async (endpoint, method = 'GET', body = null) => {
  const session = await fetchAuthSession();
  const idToken = session.tokens?.idToken?.toString();
  if (!idToken) {
    throw new Error('No authentication token available');
  }

  const token = idToken.trim();
  const url = `${API_BASE_URL}${endpoint}`;
  const options = {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  };

  if (body != null && method !== 'GET') {
    options.body = JSON.stringify(body);
  }

  let response;
  try {
    response = await fetch(url, options);
  } catch (fetchError) {
    const error = new Error(fetchError.message || 'Network error');
    error.isNetworkError = true;
    throw error;
  }

  const responseText = await response.text().catch(() => '');
  let data = {};
  if (responseText) {
    try {
      data = JSON.parse(responseText);
    } catch {
      data = { message: responseText };
    }
  }

  if (!response.ok) {
    const message =
      data?.error ||
      data?.message ||
      data?.body?.error ||
      data?.detail ||
      responseText ||
      response.statusText ||
      'Unknown error';
    const error = new Error(typeof message === 'string' ? message : JSON.stringify(message));
    error.status = response.status;
    error.responseData = data;
    error.code = data?.code || data?.error_code || data?.error || null;
    throw error;
  }

  return data;
};

/**
 * Remove nested dataUrls (API rejects them) and FE-only fields from the snapshot body.
 * Line items should carry { name, file_id } after Interior files upload on Send.
 */
export const buildApiSnapshot = (record) => {
  const stripImage = (image) => {
    if (!image || typeof image !== 'object') return null;
    if (!image.file_id && !image.s3_key && image.dataUrl) return null;
    const next = {};
    if (image.name) next.name = image.name;
    if (image.file_id) next.file_id = image.file_id;
    if (image.s3_key) next.s3_key = image.s3_key;
    return Object.keys(next).length ? next : null;
  };

  const stripItem = (item) => {
    if (!item || typeof item !== 'object') return item;
    const { collapsed, ...rest } = item;
    return {
      ...rest,
      image: stripImage(item.image),
    };
  };

  const stripRoom = (room) => {
    if (!room || typeof room !== 'object') return room;
    const { collapsed, ...rest } = room;
    return {
      ...rest,
      items: Array.isArray(room.items) ? room.items.map(stripItem) : [],
    };
  };

  const stripSection = (section) => {
    if (!section || typeof section !== 'object') return section;
    const { collapsed, ...rest } = section;
    return {
      ...rest,
      rooms: Array.isArray(section.rooms) ? section.rooms.map(stripRoom) : [],
    };
  };

  return {
    header: record?.header || {},
    sections: Array.isArray(record?.sections) ? record.sections.map(stripSection) : [],
    designFeePercent: record?.designFeePercent ?? '',
    gstPercent: record?.gstPercent ?? '18',
    paymentSchedule: Array.isArray(record?.paymentSchedule) ? record.paymentSchedule : [],
    templates: record?.templates || {},
    finalAmount: record?.finalAmount ?? 0,
  };
};

/** Pull header fields from list meta, embedded snapshot, or full snapshot. */
const extractQuotationHeader = (quotation, snapshot = null) => {
  const snap = snapshot || quotation?.snapshot || {};
  const snapHeader = snap?.header || quotation?.header;

  if (snapHeader && typeof snapHeader === 'object') {
    return {
      dateCreated:
        snapHeader.dateCreated ||
        snapHeader.date_created ||
        (quotation?.created_at || '').slice(0, 10),
      documentOwner:
        snapHeader.documentOwner ||
        snapHeader.document_owner ||
        snapHeader.docOwner ||
        '',
      client: snapHeader.client || snapHeader.client_name || snapHeader.customer || '',
      project: snapHeader.project || quotation?.project || quotation?.title || '',
      companyName: snapHeader.companyName || snapHeader.company_name || '',
    };
  }

  return {
    dateCreated: (quotation?.created_at || quotation?.date_created || '').slice(0, 10),
    documentOwner:
      quotation?.document_owner ||
      quotation?.documentOwner ||
      quotation?.doc_owner ||
      quotation?.owner_name ||
      quotation?.created_by_name ||
      '',
    client:
      quotation?.client ||
      quotation?.client_name ||
      quotation?.customer_name ||
      quotation?.customer ||
      '',
    project: quotation?.project || quotation?.title || '',
    companyName: quotation?.company_name || quotation?.companyName || '',
  };
};

const headerNeedsEnrichment = (header) =>
  !String(header?.client || '').trim() || !String(header?.documentOwner || '').trim();

/** Normalize one same-document version index entry from API JSON. */
export const mapApiVersionEntry = (entry) => {
  if (!entry || typeof entry !== 'object') return null;
  const major = Number(entry.major ?? entry.major_version ?? 0);
  const minor = Number(entry.minor ?? entry.minor_version ?? 0);
  const label =
    entry.version ||
    entry.label ||
    (Number.isFinite(major) && Number.isFinite(minor) ? `${major}.${minor}` : null);
  if (!label) return null;
  return {
    major,
    minor,
    version: String(label).replace(/^v/i, ''),
    s3Key: entry.s3_key || entry.s3Key || entry.document_s3_key || null,
    at: entry.at || entry.created_at || entry.updated_at || null,
    by: entry.by || entry.created_by || entry.user_id || null,
  };
};

/** Normalize history/audit events from GET quotation JSON. */
export const mapApiHistoryEntry = (entry) => {
  if (!entry || typeof entry !== 'object') return null;
  return {
    at: entry.at || entry.created_at || null,
    action: entry.action || entry.event || '',
    by: entry.by || entry.user_id || null,
    version: entry.version ? String(entry.version).replace(/^v/i, '') : null,
  };
};

export const normalizeVersionsList = (versions) =>
  (Array.isArray(versions) ? versions : [])
    .map(mapApiVersionEntry)
    .filter(Boolean)
    .sort((a, b) => b.major - a.major || b.minor - a.minor);

export const normalizeHistoryList = (history) =>
  (Array.isArray(history) ? history : []).map(mapApiHistoryEntry).filter(Boolean);

/** Normalize API quotation meta (+ optional snapshot) into the FE record shape. */
export const mapApiQuotationToRecord = (quotation, snapshot = null, extras = {}) => {
  const snap = snapshot || quotation?.snapshot || {};
  const major = quotation?.major_version ?? quotation?.majorVersion ?? snap?.major_version ?? 1;
  const minor = quotation?.minor_version ?? quotation?.minorVersion ?? snap?.minor_version ?? 0;
  const version =
    quotation?.version ||
    snap?.version ||
    `${major}.${minor}`;

  const versionsSource =
    extras.versions ??
    quotation?.versions ??
    snap?.versions ??
    [];
  const historySource =
    extras.history ??
    quotation?.history ??
    snap?.history ??
    [];

  return {
    id: quotation?.quote_id || quotation?.id || snap?.quote_id || extras.quoteId,
    quoteId: quotation?.quote_id || quotation?.id || snap?.quote_id || extras.quoteId,
    siteId: quotation?.site_id || extras.siteId || snap?.site_id || null,
    source: 'api',
    status: quotation?.status || extras.status || 'uploaded',
    contentType: quotation?.content_type || 'application/json',
    header: extractQuotationHeader(quotation, snap),
    sections: snap.sections || [],
    designFeePercent: snap.designFeePercent ?? '',
    gstPercent: snap.gstPercent ?? '18',
    paymentSchedule: snap.paymentSchedule || [],
    templates: snap.templates || {},
    finalAmount: quotation?.final_amount ?? quotation?.amount ?? snap.finalAmount ?? 0,
    majorVersion: major,
    minorVersion: minor,
    version: String(version).replace(/^v/i, ''),
    invoiceNumber: quotation?.invoice_number || extras.invoiceNumber || '-',
    documentS3Key: quotation?.document_s3_key || null,
    supersedesQuoteId:
      quotation?.supersedes_quote_id || quotation?.supersedesQuoteId || null,
    createdAt: quotation?.created_at || null,
    updatedAt: quotation?.updated_at || null,
    history: normalizeHistoryList(historySource),
    versions: normalizeVersionsList(versionsSource),
  };
};

export const formatApiError = (error) => {
  if (!error) return 'Something went wrong.';
  const code = error.code || error.responseData?.code || error.responseData?.error;
  const message = String(error.message || '');
  if (error.status === 409 && (String(code).includes('active_quote') || /active.?quote/i.test(message))) {
    return 'An active quotation already exists for this site. Edit the uploaded/reopened quote, or Supersede it for a new lineage.';
  }
  if (error.status === 409 && (String(code).includes('draft_exists') || /draft.?exists/i.test(message))) {
    return 'A draft already exists for this site. Edit, submit, or discard it first.';
  }
  if (error.status === 409) {
    return error.message || 'This quotation cannot be changed in its current status.';
  }
  if (error.status === 403) {
    return 'You do not have access to quotations for this site. Pick a site you are assigned to, or ask an admin to grant interior quotation access.';
  }
  if (error.status === 401) {
    return 'Session expired. Please sign in again.';
  }
  if (error.status === 400) {
    return error.message || 'Invalid quotation data. Check required fields and payment schedule.';
  }
  return error.message || 'Request failed.';
};

/** Active client-facing quote statuses (one per site). Drafts do not count. */
export const ACTIVE_QUOTE_STATUSES = ['uploaded', 'reopened'];
export const EDITABLE_QUOTE_STATUSES = ['draft', 'uploaded', 'reopened'];
export const DECIDABLE_QUOTE_STATUSES = ['uploaded', 'reopened'];
export const SUPERSEDEABLE_QUOTE_STATUSES = ['uploaded', 'reopened', 'accepted', 'rejected'];

export const isActiveQuoteStatus = (status) => ACTIVE_QUOTE_STATUSES.includes(status);
export const isEditableQuoteStatus = (status) => EDITABLE_QUOTE_STATUSES.includes(status);
export const isDecidableQuoteStatus = (status) => DECIDABLE_QUOTE_STATUSES.includes(status);
export const isSupersedeableQuoteStatus = (status) =>
  SUPERSEDEABLE_QUOTE_STATUSES.includes(status);

const quotationsBase = (siteId) =>
  `/interior/sites/${encodeURIComponent(siteId)}/quotations`;

const filesBase = (siteId) => `/interior/sites/${encodeURIComponent(siteId)}/files`;

const QUOTATION_FILE_CATEGORY = 'quotations';
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/jpg']);

/** Convert a data URL (from ImageUploadCell) to a Blob for S3 PUT. */
export const dataUrlToBlob = async (dataUrl) => {
  const response = await fetch(dataUrl);
  return response.blob();
};

/** Map file extension / blob type to API-allowed content_type. */
export const normalizeImageContentType = (fileName, blobType = '') => {
  const type = (blobType || '').toLowerCase();
  if (type === 'image/jpg') return 'image/jpeg';
  if (ALLOWED_IMAGE_TYPES.has(type)) return type;

  const ext = (fileName || '').split('.').pop()?.toLowerCase();
  if (ext === 'png') return 'image/png';
  if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg';
  return null;
};

const unwrapPayload = (data) => data?.data || data?.body || data;

/** Upload one line-item image: presign → PUT blob → register → { name, file_id }. */
export const uploadQuotationLineItemImage = async (siteId, image) => {
  if (!image) return null;
  if (image.file_id && !image.dataUrl) {
    return { name: image.name || 'image.jpg', file_id: image.file_id };
  }
  if (!image.dataUrl) return null;

  const fileName = image.name || 'image.jpg';
  const blob = await dataUrlToBlob(image.dataUrl);

  if (blob.size > MAX_IMAGE_BYTES) {
    throw new Error(`"${fileName}" exceeds the 10 MB limit.`);
  }

  const contentType = normalizeImageContentType(fileName, blob.type);
  if (!contentType) {
    throw new Error(`"${fileName}" must be PNG or JPEG.`);
  }

  const presignRaw = await makeApiRequest(`${filesBase(siteId)}/presign`, 'POST', {
    category: QUOTATION_FILE_CATEGORY,
    file_name: fileName,
    content_type: contentType,
  });
  const presign = unwrapPayload(presignRaw);
  const fileId = presign?.file_id;
  const uploadUrl = presign?.upload_url;
  const s3Key = presign?.s3_key;
  const resolvedContentType = presign?.content_type || contentType;

  if (!fileId || !uploadUrl || !s3Key) {
    throw new Error(`Failed to get upload URL for "${fileName}".`);
  }

  const putResponse = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': resolvedContentType },
    body: blob,
  });
  if (!putResponse.ok) {
    throw new Error(`Failed to upload "${fileName}" to storage (${putResponse.status}).`);
  }

  await makeApiRequest(filesBase(siteId), 'POST', {
    category: QUOTATION_FILE_CATEGORY,
    title: fileName,
    s3_key: s3Key,
    content_type: resolvedContentType,
    file_id: fileId,
  });

  return { name: fileName, file_id: fileId };
};

/** Upload all local dataUrl images in sections before sending the quotation snapshot. */
export const uploadAllQuotationImages = async (siteId, sections) => {
  const uploadItem = async (item) => {
    if (!item?.image) return item;
    const uploaded = await uploadQuotationLineItemImage(siteId, item.image);
    if (!uploaded) return { ...item, image: null };
    return { ...item, image: uploaded };
  };

  return Promise.all(
    (sections || []).map(async (section) => ({
      ...section,
      rooms: await Promise.all(
        (section.rooms || []).map(async (room) => ({
          ...room,
          items: await Promise.all((room.items || []).map(uploadItem)),
        }))
      ),
    }))
  );
};

const previewUrlCache = new Map(); // key → { url, expiresAt }
/** Presigns last ~15 min; refresh a bit early so cached links never open as AccessDenied. */
const PREVIEW_URL_TTL_MS = 12 * 60 * 1000;

/** GET /interior/sites/{site_id}/files/{file_id} → preview_url for <img src>. */
export const resolveQuotationFilePreviewUrl = async (
  siteId,
  fileId,
  { forceRefresh = false } = {}
) => {
  if (!siteId || !fileId) return null;
  const cacheKey = `${siteId}:${fileId}`;
  const cached = previewUrlCache.get(cacheKey);
  if (
    !forceRefresh &&
    cached?.url &&
    cached.expiresAt &&
    Date.now() < cached.expiresAt
  ) {
    return cached.url;
  }

  const data = await makeApiRequest(
    `${filesBase(siteId)}/${encodeURIComponent(fileId)}`,
    'GET'
  );
  const file = unwrapPayload(data)?.file || unwrapPayload(data) || data;
  const url =
    file?.preview_url ||
    file?.previewUrl ||
    file?.download_url ||
    file?.downloadUrl ||
    file?.presigned_url ||
    file?.presignedUrl ||
    file?.url ||
    null;
  if (url) {
    previewUrlCache.set(cacheKey, {
      url,
      expiresAt: Date.now() + PREVIEW_URL_TTL_MS,
    });
  } else {
    previewUrlCache.delete(cacheKey);
  }
  return url;
};

/** Fetch a (possibly CORS) image URL into a data URL for reliable PDF / html2canvas paint. */
export const fetchImageAsDataUrl = async (url) => {
  if (!url) return null;
  if (String(url).startsWith('data:')) return url;

  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch image (${response.status})`);
    }
    const blob = await response.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error('Failed to read image'));
      reader.readAsDataURL(blob);
    });
  } catch {
    // Fallback when S3 blocks fetch() but allows crossOrigin <img> paint.
    return await new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth || img.width;
          canvas.height = img.naturalHeight || img.height;
          canvas.getContext('2d').drawImage(img, 0, 0);
          resolve(canvas.toDataURL('image/jpeg', 0.85));
        } catch (err) {
          reject(err);
        }
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = url;
    });
  }
};

export const interiorQuotationApi = {
  getSites: async () => {
    const response = await makeApiRequest('/interior/sites', 'GET');
    const normalize = (response) => {
      const list = Array.isArray(response?.sites)
        ? response.sites
        : Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : [];

      return list
        .map((site) => ({
          id: resolveSiteId(site),
          name: resolveSiteName(site),
        }))
        .filter((site) => site.id)
        .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
    };
    return normalize(response);
  },

  listQuotations: async (siteId) => {
    const data = await makeApiRequest(quotationsBase(siteId), 'GET');
    const list =
      data?.quotations ||
      data?.items ||
      data?.data ||
      (Array.isArray(data) ? data : []);

    const mapped = (Array.isArray(list) ? list : []).map((q) =>
      mapApiQuotationToRecord(q, q.snapshot || null, { siteId })
    );

    // List is meta-only — client/doc owner live in snapshot; fetch detail when missing.
    const enriched = await Promise.all(
      mapped.map(async (record) => {
        if (!record.quoteId || !headerNeedsEnrichment(record.header)) {
          return record;
        }
        try {
          const detailData = await makeApiRequest(
            `${quotationsBase(siteId)}/${encodeURIComponent(record.quoteId)}`,
            'GET'
          );
          const detail = mapApiQuotationToRecord(
            detailData?.quotation || detailData,
            detailData?.snapshot,
            {
              siteId,
              history: detailData?.history || [],
              versions: detailData?.versions || [],
            }
          );
          return {
            ...record,
            header: { ...record.header, ...detail.header },
            versions: detail.versions?.length ? detail.versions : record.versions,
            history: detail.history?.length ? detail.history : record.history,
            supersedesQuoteId: detail.supersedesQuoteId ?? record.supersedesQuoteId,
          };
        } catch {
          return record;
        }
      })
    );

    return enriched;
  },

  getQuotation: async (siteId, quoteId) => {
    const data = await makeApiRequest(
      `${quotationsBase(siteId)}/${encodeURIComponent(quoteId)}`,
      'GET'
    );
    const payload = unwrapPayload(data) || data;
    return mapApiQuotationToRecord(payload?.quotation || payload, payload?.snapshot, {
      siteId,
      quoteId,
      history: payload?.history || payload?.quotation?.history || [],
      versions: payload?.versions || payload?.quotation?.versions || [],
    });
  },

  createQuotation: async (siteId, record, { draft = false } = {}) => {
    const snapshot = buildApiSnapshot(record);
    const qs = draft ? '?draft=true' : '';
    const body = draft ? { ...snapshot, status: 'draft' } : snapshot;
    const data = await makeApiRequest(`${quotationsBase(siteId)}${qs}`, 'POST', body);
    const payload = unwrapPayload(data) || data;
    return mapApiQuotationToRecord(payload?.quotation || payload, payload?.snapshot || snapshot, {
      siteId,
      history: payload?.history || [],
      versions: payload?.versions || [],
    });
  },

  updateQuotation: async (siteId, quoteId, record, { bumpMajor = false } = {}) => {
    const snapshot = buildApiSnapshot(record);
    const body = bumpMajor ? { ...snapshot, bump_major: true } : snapshot;
    const data = await makeApiRequest(
      `${quotationsBase(siteId)}/${encodeURIComponent(quoteId)}`,
      'PUT',
      body
    );
    const payload = unwrapPayload(data) || data;
    return mapApiQuotationToRecord(payload?.quotation || payload, payload?.snapshot || snapshot, {
      siteId,
      quoteId,
      history: payload?.history || [],
      versions: payload?.versions || [],
    });
  },

  /** draft → uploaded. Runs the one-active-quote check. */
  submitQuotation: async (siteId, quoteId) => {
    const data = await makeApiRequest(
      `${quotationsBase(siteId)}/${encodeURIComponent(quoteId)}/submit`,
      'POST',
      {}
    );
    return mapApiQuotationToRecord(data?.quotation || data, data?.snapshot, { siteId });
  },

  /** draft → discarded (terminal). Frees the one-draft-per-site slot. */
  discardQuotation: async (siteId, quoteId) => {
    const data = await makeApiRequest(
      `${quotationsBase(siteId)}/${encodeURIComponent(quoteId)}/discard`,
      'POST',
      {}
    );
    return mapApiQuotationToRecord(data?.quotation || data, data?.snapshot, { siteId });
  },

  /**
   * Same-document minor timeline (1.0 → 1.1 → 1.2), not the supersede major chain.
   * GET …/quotations/{quote_id}/versions
   */
  listVersions: async (siteId, quoteId) => {
    const data = await makeApiRequest(
      `${quotationsBase(siteId)}/${encodeURIComponent(quoteId)}/versions`,
      'GET'
    );
    const payload = unwrapPayload(data) || data;
    const versions = normalizeVersionsList(
      payload?.versions || payload?.items || (Array.isArray(payload) ? payload : [])
    );
    return {
      quoteId: payload?.quote_id || quoteId,
      current: String(payload?.current || versions[0]?.version || '').replace(/^v/i, ''),
      versions,
    };
  },

  /**
   * Immutable historical snapshot JSON for major.minor.
   * GET …/versions/{major}.{minor}
   */
  getVersion: async (siteId, quoteId, version) => {
    const label = String(version || '').replace(/^v/i, '');
    const data = await makeApiRequest(
      `${quotationsBase(siteId)}/${encodeURIComponent(quoteId)}/versions/${encodeURIComponent(label)}`,
      'GET'
    );
    const payload = unwrapPayload(data) || data;
    const quotation = payload?.quotation || null;
    // Response may be { snapshot } or the snapshot fields at the top level.
    const snapshot =
      payload?.snapshot ||
      (payload?.sections || payload?.header ? payload : null) ||
      {};
    const [majorStr, minorStr] = label.split('.');
    return mapApiQuotationToRecord(
      quotation || {
        quote_id: quoteId,
        site_id: siteId,
        version: label,
        major_version: Number(majorStr),
        minor_version: Number(minorStr),
        status: payload?.status,
        final_amount: payload?.final_amount ?? snapshot?.finalAmount,
        invoice_number: payload?.invoice_number,
      },
      snapshot,
      { siteId, quoteId }
    );
  },

  /**
   * Summary diff between two same-document revisions.
   * GET …/versions/{from}/diff?against={against}
   */
  getVersionDiff: async (siteId, quoteId, version, against) => {
    const from = String(version || '').replace(/^v/i, '');
    const againstLabel = against != null ? String(against).replace(/^v/i, '') : '';
    const qs = againstLabel ? `?against=${encodeURIComponent(againstLabel)}` : '';
    const data = await makeApiRequest(
      `${quotationsBase(siteId)}/${encodeURIComponent(quoteId)}/versions/${encodeURIComponent(from)}/diff${qs}`,
      'GET'
    );
    const payload = unwrapPayload(data) || data;
    const diff = payload?.diff || payload || {};
    return {
      quoteId: payload?.quote_id || quoteId,
      from: String(payload?.from || from).replace(/^v/i, ''),
      against: String(payload?.against || againstLabel).replace(/^v/i, ''),
      diff: {
        sectionsAdded: diff.sections_added || diff.sectionsAdded || [],
        sectionsRemoved: diff.sections_removed || diff.sectionsRemoved || [],
        sectionsRenamed: diff.sections_renamed || diff.sectionsRenamed || [],
        finalAmountDelta: diff.final_amount_delta ?? diff.finalAmountDelta ?? 0,
        designFeePercentChanged:
          diff.design_fee_percent_changed ?? diff.designFeePercentChanged ?? false,
        gstPercentChanged: diff.gst_percent_changed ?? diff.gstPercentChanged ?? false,
        paymentScheduleChanged:
          diff.payment_schedule_changed ?? diff.paymentScheduleChanged ?? false,
      },
    };
  },

  decideQuotation: async (siteId, quoteId, decision) => {
    const data = await makeApiRequest(
      `${quotationsBase(siteId)}/${encodeURIComponent(quoteId)}/decide`,
      'POST',
      { decision }
    );
    return mapApiQuotationToRecord(data?.quotation || data, data?.snapshot, { siteId });
  },

  reopenQuotation: async (siteId, quoteId) => {
    const data = await makeApiRequest(
      `${quotationsBase(siteId)}/${encodeURIComponent(quoteId)}/reopen`,
      'POST',
      {}
    );
    return mapApiQuotationToRecord(data?.quotation || data, data?.snapshot, { siteId });
  },

  supersedeQuotation: async (siteId, quoteId, record) => {
    const snapshot = buildApiSnapshot(record);
    const data = await makeApiRequest(
      `${quotationsBase(siteId)}/${encodeURIComponent(quoteId)}/supersede`,
      'POST',
      snapshot
    );
    return mapApiQuotationToRecord(data?.quotation || data, data?.snapshot || snapshot, {
      siteId,
    });
  },

  getFile: async (siteId, fileId) => {
    const data = await makeApiRequest(
      `${filesBase(siteId)}/${encodeURIComponent(fileId)}`,
      'GET'
    );
    return unwrapPayload(data)?.file || unwrapPayload(data) || data;
  },

  resolveFilePreviewUrl: resolveQuotationFilePreviewUrl,
};
